<script>
import { mapState } from 'vuex'
import store from '@/store/store'
import { Icon } from 'leaflet'

export default {
  name: 'BaseLeafletMapComponent',
  components: {
    LPolygon: () => import('vue2-leaflet'),
    LCircle: () => import('vue2-leaflet'),
    MapCrudComponent: () => import('@/components/map/MapCrudComponent'),
    SubmitGroup: () => import('@/components/SubmitGroup'),
    BaseDialogTitle: () => import('@/components/BaseDialogTitle')
  },
  data() {
    return {
      zoomBounds: [],
      drawnPois: new window.L.FeatureGroup(),
      showMapCrud: false,
      dialog: {
        show: false,
        component: this.MapCrudComponent,
        loading: true,
        submited: false
      },
      mapCrudData: {},
      closeOnSubmit: false,
      showLabels: false,
      poiPoints: []
    }
  },
  computed: {
    ...mapState('map', ['vehicles', 'pois'])
  },
  mounted() {
    this.$refs.map.mapObject.addControl(this.drawnPois)
    this.fixMissingMarkerIcons()
    this.$refs.map.mapObject.on('contextmenu', this.drawPoi)
  },
  methods: {
    setZoomLevelBasedOnAllVehicles() {
      this.vehicles.forEach(vehicle => {
        this.zoomBounds.push([vehicle.latitude, vehicle.longitude])
      })

      if (this.zoomBounds.length) {
        if (this.$refs.map) {
          this.$refs.map.fitBounds(this.zoomBounds)
        }
      }
    },
    getCustomMarkerIcon(icon, iconColor, sizeX, sizeY) {
      return L.icon({
        iconUrl: `${process.env.VUE_APP_S3_DOMAIN}/icons/${icon}`,
        iconSize: [sizeX, sizeY],
        iconAnchor: [20, 20],
        className: iconColor
      })
    },
    setZoomLevelToFocusedVehicles(focusAll = true) {
      this.zoomBounds = []

      this.vehicles.forEach(vehicle => {
        if (vehicle.shouldDisplay() && vehicle.focused) {
          this.zoomBounds.push([vehicle.latitude, vehicle.longitude])
        }
      })

      if (this.zoomBounds.length) {
        this.$refs.map.fitBounds(this.zoomBounds)
      } else if (focusAll) {
        this.setZoomLevelToVisibleVehicles()
      }
    },
    setZoomLevelToVisibleVehicles() {
      this.zoomBounds = []

      this.vehicles.forEach(vehicle => {
        if (vehicle.shouldDisplay()) {
          this.zoomBounds.push([vehicle.latitude, vehicle.longitude])
        }
      })

      if (this.zoomBounds.length) {
        this.$refs.map.fitBounds(this.zoomBounds)
      }
    },
    fixMissingMarkerIcons() {
      /**
       * Quick fix for missing default Marker Icons.
       * Please refer to: https://vue2-leaflet.netlify.com/quickstart/#marker-icons-are-missing
       */
      delete Icon.Default.prototype._getIconUrl
      Icon.Default.mergeOptions({
        iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
        iconUrl: require('leaflet/dist/images/marker-icon.png'),
        shadowUrl: require('leaflet/dist/images/marker-shadow.png')
      })
    },
    zoomToRoute(route) {
      if (!route.length) {
        return
      }
      this.$refs.map.fitBounds(route.map(x => [x.latitude, x.longitude]))
    },
    handleMapCrudComponentClose(notification) {
      this.mapCrudData = {}
      store.dispatch('notification/add', notification, { root: true })
      this.dialog.submited = false
      this.dialog.show = false
    },
    onCloseDialogHandler() {
      this.dialog.show = false
    },
    onSubmitDialogHandler() {
      this.dialog.submited = true
    },
    handlePoisDrawing() {
      this.drawnPois.clearLayers()
      /**
       * Loop through all objects that camed back from API.
       */
      for (var i = 0; i < this.pois.length; i++) {
        /**
         * Type of 1 is used for Polygon.
         */
        if (this.pois[i].type == 1) {
          this.handlePolygonPoiDrawing(this.pois[i])
        }

        /**
         * Type of 0 is used for Circle
         */
        if (this.pois[i].type == 0) {
          this.handleCircularPoiDrawing(this.pois[i])
        }
      }
    },
    handleCircularPoiDrawing(poi) {
      const popup = this.createPopupObject(poi)
      var circle = window.L.circle(
        [poi.coordinates[0].latitude, poi.coordinates[0].longitude],
        {
          radius: poi.radius,
          color: poi.color ? poi.color : 'green'
        }
      )
        .bindPopup(popup.content, popup.options) // Binding popup on click, to display data.
        .on('mouseover', function() {
          this.openPopup()
        })
        .on('mouseout', function() {
          this.closePopup()
        })
      /**
       * Custom properties.
       */
      circle._id = poi.id
      circle._poiName = poi.name
      circle._poiTime = poi.allowedStayMinutes
      circle._color = poi.color
      circle._restrictionType = poi.restriction
      circle._type = poi.type
      circle._vehicleIds = poi.vehicleIds
      circle._contacts = poi.contacts
      circle._hasParking = poi.hasParking
      circle._objectType = poi.objectType
      circle._unloadPlaceType = poi.unloadPlaceType
      circle._pointOfInterestType = poi.pointOfInterestType
      circle._objectCode = poi.objectCode
      circle._hasRamp = poi._hasRamp
      circle._hasForklift = poi._hasForklift
      circle._specialPermitRequired = poi._specialPermitRequired
      circle._posClientId = poi._posClientId
      circle._businessUnitId = poi._businessUnitId
      circle._maximumSpeed = poi._maximumSpeed
      circle._dwellTimeMin = poi._dwellTimeMin

      this.drawnPois.addLayer(circle)
    },
    zoomToPois() {
      this.zoomBounds = []

      this.pois.forEach(poi => {
        if (poi.type === 0) {
          poi.coordinates.forEach(coordinate => {
            this.zoomBounds.push([coordinate.latitude, coordinate.longitude])
            this.zoomBounds.push([
              coordinate.latitude + 1 / (111111 / poi.radius),
              coordinate.longitude
            ])
            this.zoomBounds.push([
              coordinate.latitude - 1 / (111111 / poi.radius),
              coordinate.longitude
            ])
          })
        } else {
          poi.coordinates.forEach(coordinate => {
            this.zoomBounds.push([coordinate.latitude, coordinate.longitude])
          })
        }
      })

      if (this.zoomBounds.length) {
        this.$refs.map.fitBounds(this.zoomBounds)
      }
    },
    handlePolygonPoiDrawing(poi) {
      const popup = this.createPopupObject(poi)
      var finalLatlngs = []
      /**
       * Here we are preparing coordinates format to be
       * compatible with what Leaflet is expecting.
       * Format is [[], []].
       */
      poi.coordinates.forEach(x => {
        finalLatlngs.push([x.latitude, x.longitude])
      })

      var polygon = window.L.polygon(finalLatlngs, {
        color: poi.color ? poi.color : 'green'
      })
        .bindPopup(popup.content, popup.options) // Binding popup on click, to display data.
        //Adding mouse over and mouse leave event
        .on('mouseover', function() {
          this.openPopup()
        })
        .on('mouseout', function() {
          this.closePopup()
        })
      /**
       * Adding some custom properties that we will use later on.
       */
      polygon._id = poi.id
      polygon._poiName = poi.name
      polygon._poiTime = poi.allowedStayMinutes
      polygon._color = poi.color // Here we need color as well for edit purposes.
      polygon._restrictionType = poi.restriction
      polygon._type = poi.type
      polygon._vehicleIds = poi.vehicleIds
      polygon._maximumSpeed = poi.maximumSpeed
      this.drawnPois.addLayer(polygon)
    },
    drawPoi(e) {
      if (this.$route.path !== '/map/poi') return
      var circle = window.L.circle([e.latlng.lat, e.latlng.lng], 500)

      circle._id = ''
      circle._poiName = ''
      circle._poiTime = ''
      circle._restrictionType = ''
      circle._color = '#1CE'
      circle._type = 0
      circle._maximumSpeed = ''

      var data = {
        circle: circle,
        eventType: 'CIRCLE'
      }

      this.drawCirclePoi(data)
    },
    drawCirclePoi(data) {
      this.mapCrudData = data
      // this.showMapCrud = true
      this.dialog.show = true
    },
    createPopup(obj) {
      let popup = L.popup(obj.options)
        .setLatLng(obj.latlng)
        .setContent(obj.content)
      this.poiPoints.push(popup)
    },
    createPopupObject(poi) {
      let latlng = L.latLng(
        poi.coordinates[0].latitude,
        poi.coordinates[0].longitude
      )
      let content = `<h3>${poi.name}</h3>`
      let options = {
        minWidth: 20,
        autoClose: false,
        closeOnClick: false
      }
      return { latlng, content, options }
    }
  },

  watch: {
    pois: {
      handler() {
        this.handlePoisDrawing()
        this.zoomToPois()
      }
    },
    showLabels: {
      handler() {
        this.pois.forEach(el => {
          const popup = this.createPopupObject(el)
          this.createPopup(popup)
        })
        if (this.showLabels) {
          for (const point of this.poiPoints) {
            this.drawnPois.addLayer(point)
          }
        } else {
          for (const point of this.poiPoints) {
            this.drawnPois.removeLayer(point)
          }
          this.poiPoints = []
        }
      }
    }
  }
}
</script>
